class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestSerial = 0;
    this.renderedQuery = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    // Clear old suggestions immediately, before SearchForm's debounced onChange.
    this.input.addEventListener('input', this.onInput.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onInput() {
    if (this.getQuery() !== this.renderedQuery) this.close();
  }

  hasCurrentResults() {
    return (
      this.getAttribute('open') === 'true' &&
      this.getAttribute('results') === 'true' &&
      !this.hasAttribute('loading') &&
      this.renderedQuery === this.getQuery()
    );
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    this.onInput();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || (this.hasCurrentResults() && this.querySelector('[aria-selected="true"] a'))) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    // Reset this form even when a suggestion in this or another search was selected.
    event.preventDefault();
    this.searchTerm = '';
    this.renderedQuery = '';
    this.close(true);
    this.toggleResetButton();
    this.input.focus();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true' && this.renderedQuery === currentSearchTerm) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        // Keep the query; let Escape also reach the enclosing desktop details-modal.
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Escape must dismiss suggestions without the native search input clearing its value.
    // Leave propagation intact so the enclosing desktop details-modal can also close.
    if (event.code === 'Escape') {
      event.preventDefault();
      return;
    }
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const query = this.querySelector('[data-fr-search-query]');
    if (query) query.textContent = newTerm;
  }

  switchOption(direction) {
    if (!this.hasCurrentResults()) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (moveUp && selectedElementIndex < 0) return;
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);

    // Scroll only the suggestions panel, never the page behind the search.
    const panel = this.predictiveSearchResults;
    const panelBounds = panel.getBoundingClientRect();
    const itemBounds = activeElement.getBoundingClientRect();
    const visibleTop = panelBounds.top + panel.clientTop;
    const visibleBottom = visibleTop + panel.clientHeight;
    if (itemBounds.top < visibleTop) panel.scrollTop -= visibleTop - itemBounds.top;
    else if (itemBounds.bottom > visibleBottom) panel.scrollTop += itemBounds.bottom - visibleBottom;
  }

  selectOption() {
    if (!this.hasCurrentResults()) return;
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestSerial = ++this.requestSerial;
    const queryKey = searchTerm.toLowerCase();
    const canRender = () =>
      requestSerial === this.requestSerial &&
      this.getQuery() === searchTerm &&
      this.contains(document.activeElement) &&
      this.input.getClientRects().length > 0;
    this.setLiveRegionLoadingState();
    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    if (this.cachedResults[queryKey]) {
      const resultsMarkup = this.cachedResults[queryKey];
      const cachedDocument = new DOMParser().parseFromString(resultsMarkup, 'text/html');
      const totalCount = parseInt(cachedDocument.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
      if (canRender()) {
        this.renderedQuery = searchTerm;
        this.renderSearchResults(resultsMarkup);
      }
      searchDeferred?.resolve({ totalCount });
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources%5Blimit%5D=4&resources%5Boptions%5D%5Bunavailable_products%5D=hide&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const resultSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!resultSection) throw new Error('Predictive search response is missing its section');
        const resultsMarkup = resultSection.innerHTML;
        const totalCount = parseInt(resultSection.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
        // Cache safely even if focus or the query changed while the response was in flight.
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        if (canRender()) {
          this.renderedQuery = searchTerm;
          this.renderSearchResults(resultsMarkup);
        }
        searchDeferred?.resolve({ totalCount });
      })
      .catch((error) => {
        searchDeferred?.reject(error);
        if (error?.name === 'AbortError' || error?.code === 20) return;
        // An older failed request must not close the current query's results.
        if (requestSerial === this.requestSerial) this.close();
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', false);
    this.input.removeAttribute('aria-activedescendant');
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.updateSearchForTerm('', this.getQuery());
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.abortController.abort();
    this.requestSerial += 1;
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.renderedQuery = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
